import { FaEnvelope, FaKeyboard, FaMicrophone, FaServer, FaSlack, FaRegPlusSquare, FaInfoCircle, FaRobot } from 'react-icons/fa';
import { Handle, Position } from 'reactflow';
import { useState, useEffect, useRef } from 'react';
import SATooltip from '../common/SATooltip';
import TaskEngineIcon from '../agents/TaskEngineIcon';
import kafkaIcon from '../../assets/img/icons/Agent_app_logos/Apache Kafka.svg';
import rabbitmqIcon from '../../assets/img/icons/Agent_app_logos/Rabbitmq.svg';

const providerLabels = {
  KAFKA: 'Kafka',
  RABBITMQ: 'RabbitMQ',
  MQTT: 'MQTT',
};

const providerIcons = {
  KAFKA: kafkaIcon,
  RABBITMQ: rabbitmqIcon,
};

const icons = {
  text: <FaKeyboard size={20} />,
  voice: <FaMicrophone size={20} />,
  email: <FaEnvelope size={20} />,
  slack: <FaSlack size={20} />,
  sftp: <FaServer size={20} />
};

const colors = {
  text: '#4880FF',
  voice: '#31C0DF',
  email: '#EA4335',
  slack: '#4A154B',
  sftp: '#8B4513'
};

const backgrounds = {
  text: '#f0f6ff',
  voice: '#eafcff',
  email: '#fdeeee',
  slack: '#f0eef0',
  sftp: '#f5f0eb'
};

export default function InputNode({ data, id, xPos, yPos, selected }) {
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [highlightColor, setHighlightColor] = useState('green');
  const [statusTooltip, setStatusTooltip] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputType = data.inputType || 'text';
  const providerKey = String(data?.triggerConfig?.provider || '').toUpperCase();
  const configuredEvent = providerLabels[providerKey];
  const configuredAppName = String(data?.triggerConfig?.connectedAppName || '').trim();
  const displayLabel = configuredEvent || data.label || `${inputType.charAt(0).toUpperCase() + inputType.slice(1)} Input`;
  const color = colors[inputType] || '#4880FF';
  const bg = backgrounds[inputType] || '#f0f6ff';
  const providerIcon = providerIcons[providerKey];
  const icon = providerIcon
    ? <img src={providerIcon} alt={configuredEvent} style={{ width: 36, height: 36, objectFit: 'contain' }} />
    : (icons[inputType] || <FaKeyboard size={26} />);

  // Handle node highlighting events
  useEffect(() => {
    function handleHighlight(e) {
      if (e.detail.nodeId === id && e.detail.nodeType === 'input') {
        setIsHighlighted(true);
        setHighlightColor(e.detail.color || 'green');
      }
    }

    function handleStopHighlight() {
      setIsHighlighted(false);
    }

    window.addEventListener('node-highlight', handleHighlight);
    window.addEventListener('node-highlight-stop-all', handleStopHighlight);
    
    return () => {
      window.removeEventListener('node-highlight', handleHighlight);
      window.removeEventListener('node-highlight-stop-all', handleStopHighlight);
    };
  }, [id]);

  // Handle status tooltip events
  useEffect(() => {
    function handleStatusTooltip(e) {
      if (e.detail.nodeId === id && e.detail.nodeType === 'input') {
        setStatusTooltip({
          status: e.detail.status,
          reason: e.detail.reason
        });
      }
    }

    function handleClearStatusTooltip() {
      setStatusTooltip(null);
    }

    window.addEventListener('node-status-tooltip', handleStatusTooltip);
    window.addEventListener('workflow-stop', handleClearStatusTooltip);
    window.addEventListener('workflow-run', handleClearStatusTooltip);
    
    return () => {
      window.removeEventListener('node-status-tooltip', handleStatusTooltip);
      window.removeEventListener('workflow-stop', handleClearStatusTooltip);
      window.removeEventListener('workflow-run', handleClearStatusTooltip);
    };
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    // Add event listener with a small delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  // Handler for toggle dropdown
  const handleToggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(prev => !prev);
  };

  // Handler for add agent button
  const handleAddAgent = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    // Find the node's DOM position (fallback to data if not available)
    const nodeRect = e.target.closest('.node-layout')?.getBoundingClientRect();
    const position = nodeRect
      ? { x: nodeRect.right, y: nodeRect.top + nodeRect.height / 2 }
      : { x: xPos || 0, y: yPos || 0 };
    window.dispatchEvent(new CustomEvent('add-agent-from-input', {
      detail: { inputNodeId: id, position }
    }));
  };

  // Handler for add task button
  const handleAddTask = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    // Find the node's DOM position (fallback to data if not available)
    const nodeRect = e.target.closest('.node-layout')?.getBoundingClientRect();
    const position = nodeRect
      ? { x: nodeRect.right, y: nodeRect.top + nodeRect.height / 2 }
      : { x: xPos || 0, y: yPos || 0 };
    window.dispatchEvent(new CustomEvent('add-task-from-input', {
      detail: { inputNodeId: id, position }
    }));
  };

  return (
    <div 
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: 'relative'
      }} 
      title={displayLabel}
    >
    <div
      className='node-layout'
      style={{
        background: bg,
        border: isHighlighted 
          ? `2px solid ${highlightColor === 'red' ? '#dc3545' : '#28a745'}` 
          : selected 
            ? `1.5px solid ${color}` 
            : `1px solid ${color}`,
        borderRadius: '12px 25px 25px 12px', 
        boxShadow: isHighlighted 
          ? `0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08)`
          : selected 
            ? `0 0 0 4px rgba(72, 128, 255, 0.22), 0 2px 8px rgba(0,0,0,0.08)`
            : '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        animation: isHighlighted ? 'pulse 1.5s ease-in-out infinite' : 'none'
      }}
      title={displayLabel}
    >
      <div className='node-icon' style={{
        color: color,
        display: 'flex',
        width: 100,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }} title={displayLabel}>
        {icon}
        {statusTooltip && (
          <div style={{
            position: 'absolute',
            top: -5,
            right: 5,
            zIndex: 10
          }}>
            <SATooltip title={statusTooltip.reason} placement="top">
              <FaInfoCircle 
                size={14} 
                style={{ 
                  color: statusTooltip.status === 'FAILED' ? '#dc3545' : '#28a745',
                  cursor: 'pointer'
                }} 
              />
            </SATooltip>
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} id="a" style={{ background: color, right: -8, width: 13, height: 13 }} title="" />
      {/* Add button with line and dropdown */}
      {(!data.hasConnection) && (
        <div 
          style={{
            position: 'absolute',
            right: -60,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            zIndex: 2
          }}
        >
          <div style={{
            width: 38,
            height: 2,
            background: color
          }} />
          
          {/* Dropdown Button */}
          <div 
            ref={dropdownRef}
            style={{
              position: 'relative',
              display: 'inline-block'
            }}
          >
            {/* Invisible connect handle over the plus icon (keeps design unchanged). */}
            <Handle
              type="source"
              position={Position.Right}
              id="inputPlusConnect"
              onClick={handleToggleDropdown}
              style={{
                position: 'absolute',
                right: -2,
                top: -2,
                width: 26,
                height: 26,
                borderRadius: 6,
                background: 'transparent',
                border: 'none',
                opacity: 0,
                zIndex: 4,
                cursor: 'crosshair'
              }}
              title=""
            />

            <FaRegPlusSquare 
              size={20} 
              onClick={handleToggleDropdown} 
              style={{ 
                color: '#4880FF', 
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.2)';
                e.currentTarget.style.color = '#3b6fe8';
                e.currentTarget.style.filter = 'drop-shadow(0 2px 4px rgba(72, 128, 255, 0.4))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.color = '#4880FF';
                e.currentTarget.style.filter = 'none';
              }}
            />

            {/* Dropdown Menu */}
            {showDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                marginTop: '8px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e2e8f0',
                minWidth: '180px',
                zIndex: 1000,
                animation: 'slideUp 0.2s ease-out',
                overflow: 'hidden'
              }}>
                {/* Dropdown Arrow */}
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '12px',
                  width: '12px',
                  height: '12px',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderBottom: 'none',
                  borderRight: 'none',
                  transform: 'rotate(45deg)',
                  zIndex: -1
                }} />
                
                {/* Add Agent Option */}
                <button
                  onClick={handleAddAgent}
                  className='bg-0 border-0 p-3 w-100 d-flex align-items-center gap-3 cursor-pointer'
                  style={{
                    transition: 'all 0.15s ease',
                    borderBottom: '1px solid #f1f5f9'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #8D33FF 0%, #7c2ae6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <FaRobot size={16} style={{ color: '#fff' }} />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: '2px'
                    }}>
                      Add Agent
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#64748b'
                    }}>
                      Create new agent
                    </div>
                  </div>
                </button>

                {/* Add Task Option */}
                <button
                  onClick={handleAddTask}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <TaskEngineIcon />
                  </div>

                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: '2px'
                    }}>
                      Add Task
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#64748b'
                    }}>
                      Create task node
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 4 }} title={displayLabel}>
        <div className=' mt-1 font-semi-bold' style={{ fontSize: 13, color: '#2c3e50', textAlign: 'center', whiteSpace: 'nowrap' }} title={displayLabel}>{displayLabel}</div>
        {configuredEvent && configuredAppName && (
          <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={configuredAppName}>{configuredAppName}</div>
        )}
      </div>
      
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
            50% {
              box-shadow: 0 0 30px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.8)' : 'rgba(40, 167, 69, 0.8)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
            100% {
              box-shadow: 0 0 20px ${highlightColor === 'red' ? 'rgba(220, 53, 69, 0.6)' : 'rgba(40, 167, 69, 0.6)'}, 0 2px 8px rgba(0,0,0,0.08);
            }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
} 